<template>
    <v-dialog v-model="showDialog" scrollable :width="$vuetify.breakpoint.thresholds.md" persistent :fullscreen="$vuetify.breakpoint.xs">
        <template #activator="{ on, attrs }">
            <v-tooltip bottom>
                <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <v-btn
                        icon
                        v-on="{ ...on, ...tooltipOn }"
                        v-bind="{ ...attrs, tooltipAttrs }"
                        :color="type === 'ADD' ? 'success' : 'warning'"
                        :disabled="computedDisableType"
                    >
                        <v-icon>{{ type === 'ADD' ? 'mdi-plus' : 'mdi-pencil' }}</v-icon>
                    </v-btn>
                </template>
                <span>{{ computedTooptip }}</span>
            </v-tooltip>
        </template>
        <v-card>
            <v-toolbar dense flat color="secondary" dark>
                <v-toolbar-title>{{ computedTitle }}</v-toolbar-title>
                <v-spacer />
                <v-btn icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-tabs v-model="tabs" slider-color="accent" show-arrows mandatory>
                <v-tab key="TOPIC">{{ $t('Einstellungen') }}</v-tab>
                <v-tab key="IMAGE">{{ $t('Bild hinzufügen') }}</v-tab>
                <v-tab key="CONTENT">{{ $t('Content verlinken') }}</v-tab>
                <!-- <v-tab key="PREVIEW">{{ $t('Vorschau') }}</v-tab> -->
            </v-tabs>
            <v-card-text class="ma-0 pa-0">
                <v-tabs-items v-model="tabs" touchless>
                    <v-tab-item key="TOPIC" eager>
                        <v-form v-model="validate">
                            <v-container fluid>
                                <v-row>
                                    <v-col :cols="12">
                                        <v-text-field
                                            :label="$t('Titel')"
                                            :hint="$t('Der Titel des Themas')"
                                            persistent-hint
                                            v-model="name"
                                            :rules="[rules('notEmpty')]"
                                        />
                                    </v-col>
                                    <v-col :cols="6">
                                        <DatePicker v-model="startDate" :min="minimumDate" :label="$t('Startdatum')" />
                                    </v-col>
                                    <v-col :cols="6">
                                        <DatePicker v-model="endDate" :min="startDate" :label="$t('Enddatum')" />
                                    </v-col>
                                    <v-col :cols="12">
                                        <v-textarea
                                            :label="$t('Beschreibung')"
                                            :hint="$t('Die Beschreibung des Themas')"
                                            persistent-hint
                                            v-model="description"
                                            no-resize
                                            :rows="3"
                                        />
                                    </v-col>
                                    <v-col :cols="12">
                                        <v-textarea
                                            :label="$t('Notizen')"
                                            :hint="$t('Notizen, nur für Lehrer sichtbar')"
                                            persistent-hint
                                            v-model="notes"
                                            no-resize
                                            :rows="3"
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                            <v-expansion-panels accordion tile>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>{{ $t('Erweiterte Einstellungen') }}</v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <v-container fluid>
                                            <v-row>
                                                <v-col :cols="6">
                                                    <v-text-field
                                                        :disabled="computedDisableSetScore"
                                                        :label="$t('Minimum Punkte')"
                                                        :hint="$t('Geben Sie eine minimale Punktzahl an, um das Thema zu bestehen')"
                                                        persistent-hint
                                                        type="number"
                                                        min="0"
                                                        :rules="[rules('notNegative')]"
                                                        v-model="minScore"
                                                    />
                                                </v-col>
                                                <v-col :cols="6">
                                                    <v-text-field
                                                        :disabled="computedDisableSetScore"
                                                        :label="$t('Maximal erreichbare Punkte')"
                                                        :hint="$t('Geben Sie die maximal erreichbare Punktzahl an')"
                                                        persistent-hint
                                                        type="number"
                                                        min="0"
                                                        :rules="[rules('notNegative')]"
                                                        v-model="score"
                                                    />
                                                </v-col>
                                                <v-col :cols="6">
                                                    <v-switch
                                                        :label="$t('Startdatum erzwingen')"
                                                        :hint="$t('Wenn das Startdatum erzwungen wird, wird das Thema erst zum Startdatum verborgen')"
                                                        persistent-hint
                                                        v-model="forceStartDate"
                                                    />
                                                </v-col>
                                                <v-col :cols="6">
                                                    <v-switch
                                                        :label="$t('Enddatum erzwingen')"
                                                        :hint="$t('Wenn das Enddatum erzwungen wird, wird das Thema zum nächsten Tag geschlossen')"
                                                        persistent-hint
                                                        v-model="forceEndDate"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>{{ $t('Erweitertes Zeitmanagement') }}</v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <v-container fluid>
                                            <v-row>
                                                <v-col :cols="6">
                                                    <v-text-field
                                                        :label="$t('Approximierte Arbeitszeit')"
                                                        :hint="$t('Geben Sie eine Zeit in Minuten an, in der das Thema abzuarbeiten ist')"
                                                        persistent-hint
                                                        type="number"
                                                        min="0"
                                                        :rules="[rules('notNegative')]"
                                                        v-model="estimatedWorkTimeInMinutes"
                                                    />
                                                </v-col>
                                                <v-col :cols="6">
                                                    <v-text-field
                                                        :label="$t('Dauer in Tagen')"
                                                        :hint="$t('Geben Sie die Dauer in Tagen an')"
                                                        persistent-hint
                                                        type="number"
                                                        min="0"
                                                        :rules="[rules('notNegative')]"
                                                        v-model="durationInDays"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>{{ $t('Sichtbarkeitsoptionen') }}</v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <v-container fluid>
                                            <v-row>
                                                <v-col :cols="6">
                                                    <v-switch
                                                        :label="$t('Vor Themabeginn verbergen')"
                                                        :hint="
                                                            $t(
                                                                'Mit dieser Einstellung können Sie Themen, welche noch nicht begonnen haben, vor dem Benutzer verbergen'
                                                            )
                                                        "
                                                        persistent-hint
                                                        v-model="hideBeforeStart"
                                                    />
                                                </v-col>
                                                <v-col :cols="6">
                                                    <v-switch
                                                        :label="$t('Dauer in Tagen verbergen')"
                                                        :hint="$t('Mit dieser Einstellung können Sie Dauer in Tagen vor dem Benutzer verbergen')"
                                                        persistent-hint
                                                        v-model="hideDurationInDays"
                                                    />
                                                </v-col>
                                                <v-col :cols="6">
                                                    <v-switch
                                                        :label="$t('Approximierte Arbeitszeit verbergen')"
                                                        :hint="$t('Mit dieser Einstellung können Sie die geschätzte Arbeitszeit vor dem Benutzer verbergen')"
                                                        persistent-hint
                                                        v-model="hideEstimatedWorkTimeInMinutes"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>{{ $t('Benutzerdefinierte Typenbezeichnung') }}</v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <v-container fluid>
                                            <v-row>
                                                <v-col :cols="12">
                                                    <v-text-field
                                                        v-model="typeName"
                                                        :label="$t('Themabezeichnung')"
                                                        :hint="computedTopicTypeNameHint"
                                                        persistent-hint
                                                        clearable
                                                    />
                                                </v-col>
                                                <v-col :xs="12" :sm="6">
                                                    <v-text-field
                                                        v-model="learningGoalTypeName"
                                                        :label="$t('Lernzielebezeichnung')"
                                                        :hint="computedLearningGoalTypeNameHint"
                                                        persistent-hint
                                                        clearable
                                                    />
                                                </v-col>
                                                <v-col :xs="12" :sm="6">
                                                    <v-text-field
                                                        v-model="activityTypeName"
                                                        :label="$t('Aktivitätenbezeichnung')"
                                                        :hint="computedActivityTypeNameHint"
                                                        persistent-hint
                                                        clearable
                                                    />
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-form>
                    </v-tab-item>
                    <v-tab-item key="IMAGE" eager>
                        <Filemanager :selectedMedia="picture" @selectedMedia="handleSelected" selectable />
                    </v-tab-item>
                    <v-tab-item key="CONTENT" eager>
                        <KnowledgeContentManager selectable v-model="knowledgeContents" />
                    </v-tab-item>
                    <!-- <v-tab-item keys="PREVIEW">
                        <AddCourseUserPreview
                            v-model="showModal"
                            :serializedData="serializedData"
                            :timeScaleInMinutes="getSelectedAdminCourse.timeScaleInMinutes"
                            :isTopic="true"
                        />
                    </v-tab-item> -->
                </v-tabs-items>
            </v-card-text>
            <v-card-actions>
                <v-btn block :color="computedSubmitButtonColor" @click="save" :disabled="!validate">
                    {{ computedSubmitButtonText }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import DatePicker from '../../Generic/DatePicker/DatePicker';
import KnowledgeContentManager from '../../KnowledgeContent/KnowledgeContentManager';
import Filemanager from '../../FileManager/Prototype/Filemanager';
// import AddCourseUserPreview from './AddCourseUserPreview';
import KnowledgeContentMixin from '../../KnowledgeContent/Mixins/KnowledgeContentMixin';
import CourseMixin from './Mixins/CourseMixin';
import moment from 'moment';

export default {
    name: 'add-update-topic',
    components: {
        DatePicker,
        KnowledgeContentManager,
        Filemanager
        // AddCourseUserPreview
    },
    mixins: [CourseMixin, KnowledgeContentMixin],
    data: () => ({
        showDialog: false,
        showModal: false,
        showAdd: false,
        validate: false,
        tabs: 0,
        id: '',
        name: '',
        description: '',
        notes: '',
        estimatedWorkTimeInMinutes: 0,
        folderId: '',
        durationInDays: '',
        forceStartDate: false,
        forceEndDate: false,
        hideBeforeStart: false,
        hideEstimatedWorkTimeInMinutes: false,
        hideDurationInDays: false,
        knowledgeContents: [],
        score: 0,
        minScore: 0,
        picture: {},
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        topicPicturePath: {},
        initialLoad: false,
        typeName: null,
        learningGoalTypeName: null,
        activityTypeName: null,
        serializedData: {}
    }),
    watch: {
        hideEstimatedWorkTimeInMinutes(value) {
            if (value) {
                this.hideFromUserOptions = true;
            }
        },
        hideDurationInDays(value) {
            if (value) {
                this.hideFromUserOptions = true;
            }
        },
        showDialog(value) {
            if (value && !this.initialLoad) {
                this.initialLoad = true;
            }
            if (value) {
                if (this.type === 'UPDATE') {
                    Object.entries(this.getSelectedTopic).forEach(([key, value]) => (this.$data[key] = value));
                } else if (this.type === 'ADD') {
                    this.startDate = this.getSelectedAdminCourse.startDate;
                    this.endDate = this.getSelectedAdminCourse.endDate;
                }
            }
        },
        startDate(value) {
            if (moment(value).isAfter(moment(this.endDate))) {
                this.endDate = moment(value).format('YYYY-MM-DD');
            }
        },
        minScore(value) {
            const toNumber = Number(value);
            if (toNumber >= this.score) {
                this.score = toNumber;
            }
        },
        tabs(value) {
            if (value === 3) {
                this.openUserPreview();
            }
        }
    },
    props: {
        type: {
            type: String,
            default: 'ADD',
            validator: (value) => ['ADD', 'UPDATE'].includes(value)
        }
    },
    computed: {
        computedDisableType() {
            if (this.type === 'ADD') {
                return false;
            }
            if (this.type === 'UPDATE') {
                return !Object.keys(this.getSelectedTopic).length > 0;
            }
            return false;
        },
        computedTooptip() {
            switch (this.type) {
                case 'ADD':
                    return this.$t('Thema erstellen');
                case 'UPDATE':
                    return this.$t('Thema bearbeiten');
            }
            return '';
        },
        computedDisableSetScore() {
            if (!this.getSelectedAdminCourse.useEvaluation) {
                return true;
            }
            return false;
        },
        computedTopicTypeNameHint() {
            return this.$t('Sie können statt `Thema` eine eigene Bezeichnung festlegen');
        },
        computedLearningGoalTypeNameHint() {
            return this.$t('Sie können statt `Lernziel` eine eigene Bezeichnung festlegen');
        },
        computedActivityTypeNameHint() {
            return this.$t('Sie können statt `Aktivität` eine eigene Bezeichnung festlegen');
        },
        computedTitle() {
            switch (this.type) {
                case 'ADD':
                    if (this.getSelectedAdminCourse.topicTypeName !== null && this.getSelectedAdminCourse.topicTypeName !== '') {
                        return `${this.getSelectedAdminCourse.topicTypeName} ${this.$t('erstellen')}`;
                    }
                    return this.$t('Thema erstellen');

                case 'UPDATE':
                    if (this.typeName !== null && this.typeName !== '') {
                        return `${this.typeName} ${this.$t('bearbeiten')}`;
                    } else if (this.getSelectedAdminCourse.learningGoalTypeName !== null && this.getSelectedAdminCourse.learningGoalTypeName !== '') {
                        return `${this.getSelectedAdminCourse.learningGoalTypeName} ${this.$t('bearbeiten')}`;
                    }
                    return this.$t('Thema bearbeiten');
            }
            return '';
        },
        computedSubmitButtonText() {
            switch (this.type) {
                case 'ADD':
                    return this.$t('Hinzufügen');
                case 'UPDATE':
                    return this.$t('Aktualisieren');
            }
            return '';
        },
        computedSubmitButtonColor() {
            switch (this.type) {
                case 'ADD':
                    return 'success';
                case 'UPDATE':
                    return 'warning';
            }
            return '';
        },
        minimumDate() {
            return moment().format('YYYY-MM-DD');
        },
        handleDurationInDays() {
            if (this.durationInDays === '') {
                return moment(this.endDate).diff(this.startDate, 'days') + 1;
            } else if (this.durationInDays !== moment(this.endDate).diff(this.startDate, 'days') + 1) {
                return this.durationInDays;
            }
            return '';
        },
        setTypeName() {
            if (this.typeName === '') {
                return null;
            }
            return this.typeName;
        },
        setLearningGoalTypeName() {
            if (this.learningGoalTypeName === '') {
                return null;
            }
            return this.learningGoalTypeName;
        },
        setActivityTypeName() {
            if (this.activityTypeName === '') {
                return null;
            }
            return this.activityTypeName;
        }
    },
    methods: {
        openUserPreview() {
            this.serializedData = this.serialize();
            this.showModal = true;
        },
        addItem() {
            this.showAdd = true;
        },
        handleSelected(value) {
            this.picture = value;
        },
        serialize() {
            return {
                id: this.id,
                name: this.name,
                description: this.description,
                notes: this.notes,
                durationInDays: this.handleDurationInDays,
                estimatedWorkTimeInMinutes: this.estimatedWorkTimeInMinutes,
                folderId: this.folderId,
                forceStartDate: this.forceStartDate,
                forceEndDate: this.forceEndDate,
                knowledgeContents: this.knowledgeContents,
                score: this.score,
                minScore: this.minScore,
                startDate: this.startDate,
                endDate: this.endDate,
                picture: this.picture,
                hideBeforeStart: this.hideBeforeStart,
                typeName: this.setTypeName,
                learningGoalTypeName: this.setLearningGoalTypeName,
                activityTypeName: this.setActivityTypeName,
                hideEstimatedWorkTimeInMinutes: this.hideEstimatedWorkTimeInMinutes,
                hideDurationInDays: this.hideDurationInDays
            };
        },
        reset() {
            this.tabs = 0;
            this.id = '';
            this.name = '';
            this.description = '';
            this.notes = null;
            this.estimatedWorkTimeInMinutes = 0;
            this.folderId = '';
            this.forceStartDate = false;
            this.forceEndDate = true;
            this.knowledgeContents = [];
            this.score = 0;
            this.minScore = 0;
            this.startDate = moment().format('YYYY-MM-DD');
            this.endDate = moment().format('YYYY-MM-DD');
            this.hideBeforeStart = false;
            this.hideEstimatedWorkTimeInMinutes = false;
            this.hideDurationInDays = false;
            this.durationInDays = '';
            this.typeName = null;
            this.learningGoalTypeName = null;
            this.activityTypeName = null;
        },
        close() {
            this.showDialog = false;
            this.$nextTick(() => this.reset());
        },
        save() {
            switch (this.type) {
                case 'ADD': {
                    this.CREATE_TOPIC(this.serialize())
                        .then((response) =>
                            this.UPDATE_COURSE_TOPICS({
                                course: this.getSelectedAdminCourse,
                                topics: [...this.getCourseTopics, response]
                            })
                        )
                        .then(() => this.close());
                    return;
                }
                case 'UPDATE': {
                    this.UPDATE_TOPIC(this.serialize()).then(() => this.close());
                    return;
                }
            }
        },
        rules(type) {
            switch (type) {
                case 'notNegative':
                    return (v) => {
                        if (v !== undefined && v !== null) {
                            const toNumber = Number(v);
                            return toNumber >= 0 || this.$t('Bitte geben Sie eine Zahl größer oder gleich 0 an');
                        }
                    };
                case 'notEmpty':
                    return (v) => !!v || this.$t('Dieses Feld darf nicht leer sein');
            }
        }
    },
    beforeMount() {
        this.GET_KNOWLEDGE_CONTENTS();
    }
};
</script>
